import { Component } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { RmsDemoProduct } from 'src/app/model/rms-demo-product';
import { RmsIdentifyRequest, RmsSimulatorService } from 'src/app/services/rms-simulator.service';

interface LogEntry {
    time: string;
    label: string;
    request: unknown;
    response?: unknown;
    error?: unknown;
}

const AUTH_KEY_STORAGE = 'rmsSimulator.authKey';
const TILL_ID_STORAGE = 'rmsSimulator.tillId';

@Component({
    selector: 'app-rms-simulator',
    templateUrl: './rms-simulator.component.html',
    styleUrls: ['./rms-simulator.component.css'],
})
export class RmsSimulatorComponent {
    authForm: FormGroup;
    identifyForm: FormGroup;
    checkoutForm: FormGroup;
    confirmForm: FormGroup;

    identifyStatus: string | null = null;
    identifyTraderDisplayName: string | null = null;
    identifyTraderMobileMasked: string | null = null;
    identifyToken: string | null = null;
    identifyExpiresAt: string | null = null;
    identifyError: string | null = null;

    checkoutId: string | null = null;
    checkoutStatus: string | null = null;
    redemptionAmount: number | null = null;
    checkoutAuthCode: string | null = null;
    checkoutError: string | null = null;

    confirmStatus: string | null = null;
    confirmErrors: string[] | null = null;
    confirmError: string | null = null;

    logs: LogEntry[] = [];
    submittingStep: 'identify' | 'checkout' | 'confirm' | null = null;

    demoProducts: RmsDemoProduct[] = environment.rmsSimulatorDemoProducts;

    constructor(
        private formBuilder: FormBuilder,
        private rmsSimulatorService: RmsSimulatorService,
    ) {
        const storedAuthKey = localStorage.getItem(AUTH_KEY_STORAGE) || '';
        const storedTillId = localStorage.getItem(TILL_ID_STORAGE) || 'SIM-TILL-01';

        this.authForm = this.formBuilder.group({
            rmsAuthKey: [storedAuthKey, Validators.required],
        });

        this.identifyForm = this.formBuilder.group({
            code: [''],
            mobileNumber: [''],
            // LP-5140: the mobile path's idempotency key. Pre-filled but left editable, exactly
            // like Checkout's own requestId, so the replay and duplicate-conflict paths can be
            // exercised deliberately by reusing a value. Prefixed IDN rather than REQ purely so
            // the two are told apart in the log; they cannot collide, since the backend's
            // uniqueness is per (wholesalerId, operationType, externalKey).
            requestId: [this.generateId('IDN')],
        });

        this.checkoutForm = this.formBuilder.group({
            requestId: [this.generateId('REQ')],
            token: [''],
            checkoutTillId: [storedTillId, Validators.required],
            cart: this.formBuilder.array([this.createCartItem()]),
        });

        this.confirmForm = this.formBuilder.group({
            saleReference: [this.generateId('REF')],
            checkoutId: [''],
            // LP-5198: populated from Checkout's own response (see submitCheckout) - still a
            // plain editable input, same as every other field here, so this simulator can
            // deliberately exercise a missing/wrong-authCode Sale Confirmation too. No
            // Validators.required here: clearing it must still allow submission so the
            // missing-authCode rejection path can be tested against the backend.
            authCode: [''],
            saleConfirmationTillId: [storedTillId, Validators.required],
            completedAt: [new Date().toISOString()],
            finalTotal: [0, Validators.required],
        });
    }

    get cart(): FormArray {
        return this.checkoutForm.get('cart') as FormArray;
    }

    createCartItem(): FormGroup {
        return this.formBuilder.group({
            supplierCode: ['', Validators.required],
            productCode: ['', Validators.required],
            barcode: ['', Validators.required],
            quantity: [1, [Validators.required, Validators.min(1)]],
            sellingPrice: [0, [Validators.required, Validators.min(0)]],
        });
    }

    addCartItem() {
        this.cart.push(this.createCartItem());
    }

    removeCartItem(index: number) {
        if (this.cart.length > 1) {
            this.cart.removeAt(index);
        }
    }

    addDemoProduct(product: RmsDemoProduct) {
        const group = this.createCartItem();
        group.patchValue({
            supplierCode: product.supplierCode,
            productCode: product.productCode,
            barcode: product.barcode,
            sellingPrice: product.sellingPrice,
            quantity: 1,
        });

        // the form always starts with one blank row - replace it rather than leaving a
        // dangling empty/invalid line in front of the demo product just added
        if (this.cart.length === 1 && this.isCartRowBlank(this.cart.at(0))) {
            this.cart.removeAt(0);
        }

        this.cart.push(group);
    }

    private isCartRowBlank(row: AbstractControl): boolean {
        const value = row.value;
        return !value.supplierCode && !value.productCode && !value.barcode;
    }

    get basketTotal(): number {
        const total = this.cart.controls.reduce((sum, ctrl) => {
            const qty = Number(ctrl.get('quantity')?.value) || 0;
            const price = Number(ctrl.get('sellingPrice')?.value) || 0;
            return sum + qty * price;
        }, 0);
        return Math.round(total * 100) / 100;
    }

    generateId(prefix: string): string {
        const globalCrypto = globalThis.crypto as Crypto | undefined;
        const unique = globalCrypto?.randomUUID
            ? globalCrypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return `${prefix}-SIM-${unique}`;
    }

    resetTokenExpiryWarning(): boolean {
        if (!this.identifyExpiresAt) return false;
        return new Date(this.identifyExpiresAt).getTime() < Date.now();
    }

    private persistAuthDetails() {
        localStorage.setItem(AUTH_KEY_STORAGE, this.authForm.value.rmsAuthKey || '');
        localStorage.setItem(TILL_ID_STORAGE, this.checkoutForm.value.checkoutTillId || '');
    }

    private log(label: string, request: unknown, response?: unknown, error?: unknown) {
        this.logs.unshift({
            time: new Date().toLocaleTimeString(),
            label,
            request,
            response,
            error,
        });
    }

    /**
     * The backend's shared RMS error envelope is {status, errorCode, message} (RmsErrorResponse)
     * - prefer its message, since that's the one written to be safe/meaningful to show
     * externally, before falling back to whatever HttpClient/the browser gives us for a request
     * that never reached that envelope at all (network failure, CORS, a non-RMS 4xx/5xx).
     */
    private describeError(err: any): string {
        return err?.error?.message || err?.message || 'Request failed - see the log below for details.';
    }

    submitIdentify() {
        if (this.authForm.invalid) return;
        this.persistAuthDetails();
        this.identifyError = null;

        // Blank fields are sent as undefined, not '', so they are omitted from the JSON entirely.
        // The backend requires EXACTLY ONE of code/mobileNumber, and an empty string would still
        // have to be canonicalized away server-side before that check could pass.
        const body: RmsIdentifyRequest = {
            code: this.identifyForm.value.code || undefined,
            mobileNumber: this.identifyForm.value.mobileNumber || undefined,
            requestId: this.identifyForm.value.requestId || undefined,
        };

        this.submittingStep = 'identify';
        this.rmsSimulatorService.identify(this.authForm.value.rmsAuthKey, body).subscribe({
            next: (res) => {
                this.identifyStatus = res.status;
                // LP-5197: cashier display/visual-confirmation only - never fed into any later
                // form field. Session/identity authority for Checkout remains res.token alone.
                this.identifyTraderDisplayName = res.trader?.displayName ?? null;
                this.identifyTraderMobileMasked = res.trader?.mobileNumberMasked ?? null;
                this.identifyToken = res.token;
                this.identifyExpiresAt = res.expiresAt;
                this.checkoutForm.patchValue({ token: res.token });
                this.log('Identify', body, res);
                this.submittingStep = null;
            },
            error: (err) => {
                this.identifyStatus = null;
                this.identifyTraderDisplayName = null;
                this.identifyTraderMobileMasked = null;
                this.identifyToken = null;
                this.identifyExpiresAt = null;
                this.identifyError = this.describeError(err);
                // a failed re-identify must not leave the previous (possibly a different
                // customer's, possibly expired) token usable for checkout
                this.checkoutForm.patchValue({ token: '' });
                this.log('Identify', body, undefined, err?.error || err?.message || err);
                this.submittingStep = null;
            },
        });
    }

    submitCheckout() {
        if (this.checkoutForm.invalid || this.authForm.invalid) return;
        this.persistAuthDetails();
        this.checkoutError = null;

        const body = {
            requestId: this.checkoutForm.value.requestId,
            token: this.checkoutForm.value.token,
            checkoutTillId: this.checkoutForm.value.checkoutTillId,
            basketTotal: this.basketTotal,
            cart: this.cart.value,
        };

        this.submittingStep = 'checkout';
        this.rmsSimulatorService.checkout(this.authForm.value.rmsAuthKey, body).subscribe({
            next: (res) => {
                this.checkoutId = res.checkoutId;
                this.checkoutStatus = res.status;
                this.redemptionAmount = res.redemptionAmount;
                this.checkoutAuthCode = res.authCode;
                // sales/confirm's finalTotal is what the customer actually pays, not the raw
                // basket - a real till nets off whatever redemption this checkout resolved
                // (basketTotal - redemptionAmount), or the backend rejects it as a mismatch.
                const finalTotal =
                    Math.round((this.basketTotal - (res.redemptionAmount || 0)) * 100) / 100;
                this.confirmForm.patchValue({
                    checkoutId: res.checkoutId,
                    // LP-5198: authCode is now a required Sale Confirmation credential, not
                    // informational-only - carried straight from Checkout's own response, same
                    // as checkoutId, so a normal happy-path run through the simulator confirms
                    // cleanly. Still editable afterward for a deliberate wrong-authCode test.
                    authCode: res.authCode,
                    finalTotal,
                    // completedAt's constructor/New-Checkout default is captured before identify
                    // and checkout even run, so it's already stale by the time this step is
                    // reached - refresh it to reflect when the sale actually completed. Still
                    // editable afterward for a deliberate out-of-order-timestamp test.
                    completedAt: new Date().toISOString(),
                });
                this.log('Checkout', body, res);
                this.submittingStep = null;
            },
            error: (err) => {
                this.checkoutId = null;
                this.checkoutStatus = null;
                this.redemptionAmount = null;
                this.checkoutAuthCode = null;
                this.checkoutError = this.describeError(err);
                // a failed re-checkout must not leave an earlier successful checkout's id/total/
                // authCode sitting in step 3, ready to be confirmed against the wrong checkout
                this.confirmForm.patchValue({ checkoutId: '', authCode: '', finalTotal: 0 });
                this.log('Checkout', body, undefined, err?.error || err?.message || err);
                this.submittingStep = null;
            },
        });
    }

    submitConfirmSale() {
        if (this.confirmForm.invalid || this.authForm.invalid) return;
        this.persistAuthDetails();
        this.confirmError = null;
        this.confirmStatus = null;
        this.confirmErrors = null;

        const body = {
            saleReference: this.confirmForm.value.saleReference,
            checkoutId: this.confirmForm.value.checkoutId,
            authCode: this.confirmForm.value.authCode,
            saleConfirmationTillId: this.confirmForm.value.saleConfirmationTillId,
            completedAt: this.confirmForm.value.completedAt,
            finalTotal: Number(this.confirmForm.value.finalTotal),
        };

        this.submittingStep = 'confirm';
        this.rmsSimulatorService.confirmSale(this.authForm.value.rmsAuthKey, body).subscribe({
            next: (res) => {
                this.confirmStatus = res.status;
                this.confirmErrors = res.errors?.length ? res.errors : null;
                this.log('Sales Confirm', body, res);
                this.submittingStep = null;
            },
            error: (err) => {
                this.confirmError = this.describeError(err);
                this.log('Sales Confirm', body, undefined, err?.error || err?.message || err);
                this.submittingStep = null;
            },
        });
    }

    startNewCheckout() {
        this.identifyStatus = null;
        this.identifyTraderDisplayName = null;
        this.identifyTraderMobileMasked = null;
        this.identifyToken = null;
        this.identifyExpiresAt = null;
        this.identifyError = null;
        this.checkoutId = null;
        this.checkoutStatus = null;
        this.redemptionAmount = null;
        this.checkoutAuthCode = null;
        this.checkoutError = null;
        this.confirmStatus = null;
        this.confirmErrors = null;
        this.confirmError = null;

        // A new checkout is a new logical capture attempt, so it needs its own idempotency key --
        // carrying the previous one over would replay that identify instead of running a new one.
        this.identifyForm.reset({
            code: '',
            mobileNumber: '',
            requestId: this.generateId('IDN'),
        });

        this.checkoutForm.patchValue({ requestId: this.generateId('REQ'), token: '' });
        this.cart.clear();
        this.cart.push(this.createCartItem());

        this.confirmForm.patchValue({
            saleReference: this.generateId('REF'),
            checkoutId: '',
            authCode: '',
            completedAt: new Date().toISOString(),
            finalTotal: 0,
        });
    }

    clearLogs() {
        this.logs = [];
    }
}
